import React, { useCallback, useEffect, useMemo, Suspense, lazy } from 'react';
import { BrowserRouter } from 'react-router-dom';
import {
  popupStore,
  parseQuery,
  applicationStore,
  DefaultQueryParams,
  PlatformMessagesWrapper,
  PostMessageListenerMessages,
  GameLaunchManager,
  LoadingContainer,
} from '@pascal/pascal-core';
import { observer } from 'mobx-react-lite';
import {
  AppDefaultValuesEnum,
  oddballBetShopGameId,
  RoutePaths,
  socketEventsHandlerDelays,
} from './enums';
import { FactoryRouter, SocketConnection } from './core';
import { gameLiveConnection } from './services';
import { doBetStore } from './store';

const Stream = lazy(() => import('./containers/stream'));
const BetShop = lazy(() => import('./components/betShopComponents/game'));
const TerminalView = lazy(() => import('./containers/terminal'));
const GameWebMobileContainer = lazy(() =>
  import('./containers/oddBallBogContainers/gameWebMobileContainer')
);

let timeoutId;

const App = observer(() => {
  const { autoBetData } = doBetStore;
  const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
  const isTerminal = parseQuery(window.location.search)[DefaultQueryParams.IsCashDesk] === 'true';
  const isShowStream = parseQuery(window.location.search)?.isShowStream === 'true';
  const isBetShop = +gameId === oddballBetShopGameId;

  const components = useMemo(
    () => [
      ...(!isBetShop
        ? [
            {
              path: RoutePaths.game,
              element: (
                <Suspense fallback={null}>
                  <GameWebMobileContainer />
                </Suspense>
              ),
            },
            {
              path: RoutePaths.replay,
              element: (
                <Suspense fallback={null}>
                  <GameWebMobileContainer />
                </Suspense>
              ),
            },
            {
              path: RoutePaths.stream,
              element: (
                <Suspense fallback={null}>
                  <Stream />
                </Suspense>
              ),
              defaultRedirectComponent: isShowStream && isTerminal,
            },
            {
              path: RoutePaths.terminal,
              element: (
                <Suspense fallback={null}>
                  <TerminalView />
                </Suspense>
              ),
              defaultRedirectComponent: isTerminal,
            },
          ]
        : [
            {
              path: RoutePaths.game,
              element: (
                <Suspense fallback={null}>
                  <BetShop />
                </Suspense>
              ),
              defaultRedirectComponent: true,
            },
          ]),
    ],
    [isTerminal, isShowStream, isBetShop]
  );

  const reconnectInternet = useCallback(() => {
    if (!popupStore.connectionError) {
      clearTimeout(timeoutId);
      popupStore.toggleReconnect(false);
    }
  }, [popupStore.connectionError]);

  const lostInternetConnection = useCallback(() => {
    popupStore.toggleReconnect(true);
    timeoutId = setTimeout(() => {
      popupStore.toggleReconnect(false);
      popupStore.onConnectionError(true);
      gameLiveConnection.stop();
    }, 5000);
  }, []);

  useEffect(() => {
    window.addEventListener('offline', lostInternetConnection);
    window.addEventListener('online', reconnectInternet);
    return () => {
      window.removeEventListener('offline', lostInternetConnection);
      window.removeEventListener('online', reconnectInternet);
    };
  }, [lostInternetConnection, reconnectInternet]);

  useEffect(() => {
    applicationStore.setIsBogLine(!isBetShop);
    const intervalId = setInterval(() => {
      return (
        window.location.pathname === RoutePaths.game && !isBetShop && gameLiveConnection.sendPing()
      );
    }, 3000);

    return () => {
      intervalId && clearInterval(intervalId);
    };
  }, []);

  const conditionForPlatform = isBetShop || isTerminal || isShowStream;
  return (
    <BrowserRouter>
      <LoadingContainer
        appDefaultValuesEnum={AppDefaultValuesEnum}
        gameSpecificRepliedCallback={doBetStore.setReplayDataConfigs}
      >
        <PlatformMessagesWrapper
          additionalMessagesParams={[
            {
              name: PostMessageListenerMessages.StopAutoSpins,
              callback: () => {
                doBetStore.resetAutoBet();
              },
            },
            {
              name: PostMessageListenerMessages.UpdateBalance,
              callback: () => {
                gameLiveConnection.getBalance();
              },
            },
          ]}
          isBetShop={conditionForPlatform}
          autoPlayOnAndOffState={!conditionForPlatform && autoBetData.isAutoBet}
        >
          <GameLaunchManager
            socketEventsHandlerDelays={socketEventsHandlerDelays}
            GameContainer={<FactoryRouter components={components} />}
            SocketConnectionWr={
              <SocketConnection>
                <FactoryRouter components={components} />
              </SocketConnection>
            }
          />
        </PlatformMessagesWrapper>
      </LoadingContainer>
    </BrowserRouter>
  );
});

export default App;
